import React, { memo, useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTableContext } from '../../context/TableContext';

/**
 * Difficulty options with their colors
 */
const DIFFICULTY_OPTIONS = [
  { id: 'very_easy', name: 'Very Easy', color: '#22c55e' },
  { id: 'easy', name: 'Easy', color: '#84cc16' },
  { id: 'medium', name: 'Medium', color: '#f59e0b' },
  { id: 'hard', name: 'Hard', color: '#f97316' },
  { id: 'extreme', name: 'Extreme', color: '#ef4444' },
  { id: null, name: 'None', color: '#6b7280' },
];

/**
 * Get difficulty config by ID
 */
const getDifficultyConfig = (difficultyId) => {
  return DIFFICULTY_OPTIONS.find(d => d.id === difficultyId) || DIFFICULTY_OPTIONS[5]; // Default to 'None'
};

/**
 * Difficulty cell with dropdown for difficulty selection
 * Memoized to prevent re-renders when other cells change
 */
const DifficultyCell = memo(({ row }) => {
  const { onDifficultyChange } = useTableContext();
  const task = row.original;

  // Get difficulty config - memoized
  const difficultyConfig = useMemo(() => getDifficultyConfig(task.difficulty), [task.difficulty]);

  // Dropdown state
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  
  // Unique ID for this dropdown instance
  const dropdownId = useRef(`difficulty-${task.id}`).current;

  // Close dropdown on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        !buttonRef.current?.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on scroll
  useEffect(() => {
    if (!isOpen) return;
    
    const handleScroll = () => setIsOpen(false);
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen]);

  // Close when another dropdown opens (only listen when this menu is open)
  useEffect(() => {
    if (!isOpen) return;
    
    const handleCloseDropdowns = (e) => {
      if (e.detail?.exceptId !== dropdownId) {
        setIsOpen(false);
      }
    };
    
    window.addEventListener('closeTableDropdowns', handleCloseDropdowns);
    return () => window.removeEventListener('closeTableDropdowns', handleCloseDropdowns);
  }, [isOpen, dropdownId]);

  const handleButtonClick = useCallback((e) => {
    e.stopPropagation();
    if (!isOpen && buttonRef.current) {
      // Close other dropdowns before opening this one
      window.dispatchEvent(new CustomEvent('closeTableDropdowns', { detail: { exceptId: dropdownId } }));
      
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
    }
    setIsOpen(prev => !prev);
  }, [isOpen, dropdownId]);

  const handleDifficultySelect = useCallback((difficultyId) => {
    onDifficultyChange?.(task.id, difficultyId);
    setIsOpen(false);
  }, [onDifficultyChange, task.id]);

  // Memoize button styles
  const buttonStyle = useMemo(() => ({
    background: `${difficultyConfig.color}15`,
    color: difficultyConfig.color,
    borderColor: `${difficultyConfig.color}30`,
  }), [difficultyConfig.color]);

  return (
    <>
      <button
        ref={buttonRef}
        className="table-view__difficulty-btn"
        style={buttonStyle}
        onClick={handleButtonClick}
      >
        <span 
          className="table-view__difficulty-dot" 
          style={{ background: difficultyConfig.color }} 
        />
        {difficultyConfig.name}
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className="table-view__dropdown table-view__dropdown--difficulty"
            style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
            onClick={(e) => e.stopPropagation()}
          >
            {DIFFICULTY_OPTIONS.map((option) => (
              <div
                key={option.id ?? 'none'}
                className={`table-view__dropdown-item table-view__dropdown-item--difficulty ${
                  task.difficulty === option.id ? 'table-view__dropdown-item--active' : ''
                }`}
                onClick={() => handleDifficultySelect(option.id)}
              >
                <span className="table-view__difficulty-dot" style={{ background: option.color }} />
                <span className="table-view__difficulty-name">{option.name}</span>
              </div>
            ))}
          </div>,
          document.body
        )}
    </>
  );
}, (prevProps, nextProps) => {
  const prevTask = prevProps.row.original;
  const nextTask = nextProps.row.original;
  return prevTask.id === nextTask.id && prevTask.difficulty === nextTask.difficulty;
});

export default DifficultyCell;
